import { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiError, downloadFile } from "@/lib/api";

/** The compliance workbook: take it out pre-filled, fill the new days, bring it back. */
export const ComplianceTemplateDownload = ({ units = [], plantNames = {}, myPlant }) => {
  const [unit, setUnit] = useState(myPlant || "");
  const [busy, setBusy] = useState(false);

  const pull = async () => {
    setBusy(true);
    try {
      const label = unit === "ALL" ? "SAIL" : unit;
      await downloadFile(
        "/compliance/template",
        { plant: unit === "ALL" ? "" : unit },
        `${label} Dx Compliance.xlsx`,
      );
      toast.success(`${label} compliance workbook ready — fill the days and bring it back.`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="border border-border bg-card p-5 sm:flex sm:items-center sm:justify-between sm:gap-6"
      data-testid="compliance-template-download"
    >
      <div className="max-w-2xl">
        <div className="label-caps">Bulk update · compliance</div>
        <h2 className="font-display mt-1 text-base font-bold sm:text-lg">
          One sheet per unit, one row per usecase per day
        </h2>
        <p className="mt-2 text-xs text-muted-foreground">
          Every day the portal holds comes down pre-filled, with blank rows under each usecase for
          the days you are adding, and the three settle-once columns — <b>Target KPI</b>,{" "}
          <b>Baseline KPI</b> and <b>Target Impact (₹ Lakh)</b> — beside them. A usecase at the hot
          trial or beyond that is not yet on compliance watch comes down blank, so you declare its
          target in the same file you fill its first reading in. Bring it back on{" "}
          <b>Data files</b>: you see exactly which days are new, which change and which rows are
          refused, before anything is saved.
        </p>
      </div>
      <div className="mt-4 flex shrink-0 items-center gap-3 sm:mt-0">
        <Select value={unit} onValueChange={setUnit}>
          <SelectTrigger
            className="h-11 w-[190px] rounded-none"
            data-testid="compliance-template-unit"
          >
            <SelectValue placeholder="Which unit?" />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-popover">
            {!myPlant && units.length > 1 && <SelectItem value="ALL">All units, one file</SelectItem>}
            {units.map((u) => (
              <SelectItem key={u} value={u}>
                {u} — {plantNames[u] || u}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          className="h-11 rounded-none font-display text-xs font-bold uppercase tracking-widest"
          data-testid="download-compliance-template"
          disabled={busy || !unit}
          onClick={pull}
        >
          {busy ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Download className="mr-2 h-4 w-4" />
          )}
          Download
        </Button>
      </div>
    </div>
  );
};
